import { Text as T } from '@eo-locale/preact';
import { useRef } from 'preact/hooks';
import { useCallback } from 'react';
import { Review } from 'types/review';
import { ExtendedFullPageWidgetSettings } from 'types/settings';
import { formatDate, useImage } from 'utilities/content';
import { useLocale } from 'utilities/use-locale';
import { useToggle } from 'utilities/use-toggle';

import { Card } from 'components/Card';
import { Image } from 'components/Image';
import ReviewModal from 'components/ReviewModal';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';

import { Footer } from './components/Footer';
import { Header } from './components/Header';

import styles from './ReviewCard.module.scss';

interface ReviewCardProps {
  review: Review;
}

export function ReviewCard({ review }: ReviewCardProps) {
  const {
    title,
    author,
    created_at,
    location,
    rating,
    body,
    images,
    verified_purchase,
    pros,
    cons,
    product_title,
    product_image_src,
  } = review;
  const { show_location, show_thumbnail, enable_show_full_review, settings } =
    useSettings<ExtendedFullPageWidgetSettings>();

  const customDateFormat = settings.reviews.tweaks.custom_date_format_json;

  const { locale } = useLocale();

  const ref = useRef<HTMLDivElement>(null);

  const {
    value: open,
    setTrue: openModal,
    setFalse: closeModal,
  } = useToggle(false);

  const prosMarkup = pros ? (
    <Stack spacing="extraTight" wrap={false} alignment="baseline">
      <Text size="small" variation="strong" as="span">
        <T id="review_pros_label" defaultMessage="Pros" />:
      </Text>
      <Stack.Item fill>
        <Text size="small" lineClamp={1}>
          {pros}
        </Text>
      </Stack.Item>
    </Stack>
  ) : null;

  const consMarkup = cons ? (
    <Stack spacing="extraTight" alignment="baseline">
      <Text size="small" variation="strong" as="span">
        <T id="review_cons_label" defaultMessage="Cons" />:
      </Text>
      <Stack.Item fill>
        <Text size="small" lineClamp={1}>
          {cons}
        </Text>
      </Stack.Item>
    </Stack>
  ) : null;

  const { getImageUrl } = useImage();

  const coverImage = images.length
    ? getImageUrl(images[0].image_key, '400')
    : null;

  const onLoad = useCallback(() => {
    if (ref.current) {
      const event = new Event('wbtxImageLoaded', { bubbles: true });

      ref.current.dispatchEvent(event);
    }
  }, [ref]);

  const imagesMarkup = coverImage && (
    <div className={styles.Image}>
      <Image source={coverImage} alt={product_title} onLoad={onLoad} />
    </div>
  );

  return (
    <div ref={ref} class="wbtxMasonryItem">
      {open && (
        <ReviewModal
          review={review}
          contentExpanded={!enable_show_full_review}
          settings={{ show_location, show_thumbnail }}
          open={open}
          onClose={closeModal}
        />
      )}
      <div className={styles.Card} onClick={openModal}>
        <Card>
          {imagesMarkup}
          <Card.Section>
            <Stack vertical>
              <Header
                author={author}
                title={title}
                rating={rating}
                verified={verified_purchase === 'VERIFIED'}
                publishDate={formatDate(created_at, locale, customDateFormat)}
                location={show_location && location ? location : undefined}
              />
              <Stack.Item fill>
                <Stack vertical spacing="tight">
                  <Text size="small" lineClamp={3}>
                    {body}
                  </Text>
                  {prosMarkup}
                  {consMarkup}
                </Stack>
              </Stack.Item>
              <Footer product_title={product_title} img={product_image_src} />
            </Stack>
          </Card.Section>
        </Card>
      </div>
    </div>
  );
}
